import { supabase } from '../lib/supabase';

export interface Plan {
  id: string;
  name: string;
  slug: string;
  description: string;
  priceMonthly: number;
  priceYearly: number;
  dailyMessageLimit: number | null; // null = ilimitado
  features: string[];
  isActive: boolean;
  stripeProductId?: string;
  stripePriceIdMonthly?: string;
  stripePriceIdYearly?: string;
}

export interface Subscription {
  id: string;
  tenantId: string;
  planId: string;
  stripeSubscriptionId?: string;
  stripeCustomerId?: string;
  status: 'active' | 'cancelled' | 'past_due' | 'unpaid';
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  cancelledAt?: Date;
  metadata: Record<string, any>;
}

export interface UsageSummary {
  tenantId: string;
  userId: string;
  date: string;
  totalMessages: number;
  limitMessages: number | null;
  isUnlimited: boolean;
  canSendMore: boolean;
}

class SimplePlansService {
  
  // =====================================================
  // GESTÃO DE PLANOS
  // =====================================================

  /**
   * Buscar todos os planos disponíveis
   */
  async getPlans(): Promise<Plan[]> {
    try {
      const { data, error } = await supabase
        .from('plans')
        .select('*')
        .eq('is_active', true)
        .order('price_monthly', { ascending: true });

      if (error) throw error;

      return (data || []).map(plan => ({
        id: plan.id,
        name: plan.name,
        slug: plan.slug,
        description: plan.description,
        priceMonthly: parseFloat(plan.price_monthly),
        priceYearly: parseFloat(plan.price_yearly),
        dailyMessageLimit: plan.daily_message_limit,
        features: Array.isArray(plan.features) ? plan.features : JSON.parse(plan.features || '[]'),
        isActive: plan.is_active,
        stripeProductId: plan.stripe_product_id,
        stripePriceIdMonthly: plan.stripe_price_id_monthly,
        stripePriceIdYearly: plan.stripe_price_id_yearly
      }));
    } catch (error) {
      console.error('❌ Erro ao buscar planos:', error);
      return [];
    }
  }

  /**
   * Buscar plano por slug
   */
  async getPlanBySlug(slug: string): Promise<Plan | null> {
    try {
      const { data, error } = await supabase
        .from('plans')
        .select('*')
        .eq('slug', slug)
        .eq('is_active', true)
        .single();

      if (error || !data) return null;

      return {
        id: data.id,
        name: data.name,
        slug: data.slug,
        description: data.description,
        priceMonthly: parseFloat(data.price_monthly),
        priceYearly: parseFloat(data.price_yearly),
        dailyMessageLimit: data.daily_message_limit,
        features: Array.isArray(data.features) ? data.features : JSON.parse(data.features || '[]'),
        isActive: data.is_active,
        stripeProductId: data.stripe_product_id,
        stripePriceIdMonthly: data.stripe_price_id_monthly,
        stripePriceIdYearly: data.stripe_price_id_yearly
      };
    } catch (error) {
      console.error('❌ Erro ao buscar plano:', error);
      return null;
    }
  }

  // =====================================================
  // GESTÃO DE ASSINATURAS
  // =====================================================

  /**
   * Buscar assinatura ativa de um tenant
   */
  async getTenantSubscription(tenantId: string): Promise<Subscription | null> {
    try {
      const { data, error } = await supabase
        .from('subscriptions')
        .select(`
          *,
          plan:plans(*)
        `)
        .eq('tenant_id', tenantId)
        .eq('status', 'active')
        .single();

      if (error || !data) return null;

      return {
        id: data.id,
        tenantId: data.tenant_id,
        planId: data.plan_id,
        stripeSubscriptionId: data.stripe_subscription_id,
        stripeCustomerId: data.stripe_customer_id,
        status: data.status,
        currentPeriodStart: new Date(data.current_period_start),
        currentPeriodEnd: new Date(data.current_period_end),
        cancelledAt: data.cancelled_at ? new Date(data.cancelled_at) : undefined,
        metadata: data.metadata || {}
      };
    } catch (error) {
      console.error('❌ Erro ao buscar assinatura:', error);
      return null;
    }
  }

  /**
   * Criar assinatura para um tenant
   */
  async createSubscription(
    tenantId: string, 
    planSlug: string, 
    stripeData?: {
      subscriptionId: string;
      customerId: string;
    }
  ): Promise<Subscription | null> {
    try {
      const plan = await this.getPlanBySlug(planSlug);
      if (!plan) throw new Error('Plano não encontrado');

      // Cancelar assinatura ativa existente
      await this.cancelTenantSubscription(tenantId);

      const subscriptionData = {
        tenant_id: tenantId,
        plan_id: plan.id,
        stripe_subscription_id: stripeData?.subscriptionId,
        stripe_customer_id: stripeData?.customerId,
        status: 'active',
        current_period_start: new Date().toISOString().split('T')[0],
        current_period_end: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        metadata: {
          created_via: 'api',
          plan_slug: planSlug
        }
      };

      const { data, error } = await supabase
        .from('subscriptions')
        .insert(subscriptionData)
        .select()
        .single();

      if (error) throw error;

      // Atualizar plano do tenant
      await supabase
        .from('tenants')
        .update({ 
          plan: plan.name,
          subscription_id: data.id 
        })
        .eq('id', tenantId);

      return {
        id: data.id,
        tenantId: data.tenant_id,
        planId: data.plan_id,
        stripeSubscriptionId: data.stripe_subscription_id,
        stripeCustomerId: data.stripe_customer_id,
        status: data.status,
        currentPeriodStart: new Date(data.current_period_start),
        currentPeriodEnd: new Date(data.current_period_end),
        cancelledAt: data.cancelled_at ? new Date(data.cancelled_at) : undefined,
        metadata: data.metadata || {}
      };

    } catch (error) {
      console.error('❌ Erro ao criar assinatura:', error);
      return null;
    }
  }

  /**
   * Cancelar assinatura de um tenant
   */
  async cancelTenantSubscription(tenantId: string): Promise<void> {
    try {
      await supabase
        .from('subscriptions')
        .update({ 
          status: 'cancelled', 
          cancelled_at: new Date().toISOString() 
        })
        .eq('tenant_id', tenantId)
        .eq('status', 'active');
    } catch (error) {
      console.error('❌ Erro ao cancelar assinatura:', error);
    }
  }

  // =====================================================
  // CONTROLE DE USO E LIMITES
  // =====================================================

  /**
   * Verificar se usuário pode enviar mensagem
   */
  async canSendMessage(tenantId: string, userId: string): Promise<boolean> {
    try {
      // Verificar plano diretamente no tenant
      const { data: tenantData } = await supabase
        .from('tenants')
        .select('plan')
        .eq('id', tenantId)
        .single();
      
      // Se for Premium, permitir sempre
      if (tenantData?.plan === 'premium') {
        return true;
      }
      
      // Se for Free, verificar limite diário
      const usage = await this.getDailyUsage(tenantId, userId);
      return usage.canSendMore;

    } catch (error) {
      console.error('❌ Erro ao verificar limite:', error);
      return true; // Em caso de erro, permitir envio
    }
  }

  /**
   * Buscar uso diário de um usuário
   */
  async getDailyUsage(tenantId: string, userId: string): Promise<UsageSummary> {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Buscar apenas o registro consolidado com provider 'all'
      const { data, error } = await supabase
        .from('daily_usage')
        .select('messages_sent')
        .eq('tenant_id', tenantId)
        .eq('user_id', userId)
        .eq('usage_date', today)
        .eq('ai_provider', 'all')
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('❌ Erro ao buscar uso diário:', error);
      }

      const totalMessages = data?.messages_sent || 0;
      
      // Verificar se tem plano premium diretamente no tenant
      const { data: tenantData } = await supabase
        .from('tenants')
        .select('plan')
        .eq('id', tenantId)
        .single();
      
      const isUnlimited = tenantData?.plan === 'premium';
      
      // Verificação de plano
      
      const limitMessages = isUnlimited ? null : 10; // Freemium = 10 mensagens
      const canSendMore = isUnlimited || totalMessages < 10;

      return {
        tenantId,
        userId,
        date: today,
        totalMessages,
        limitMessages,
        isUnlimited: !!isUnlimited,
        canSendMore
      };

    } catch (error) {
      console.error('❌ Erro ao buscar uso diário:', error);
      return {
        tenantId,
        userId,
        date: new Date().toISOString().split('T')[0],
        totalMessages: 0,
        limitMessages: 10,
        isUnlimited: false,
        canSendMore: true
      };
    }
  }

  /**
   * Incrementar uso diário
   */
  async incrementUsage(tenantId: string, userId: string, aiProvider: string = 'gpt'): Promise<void> {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Primeiro, tentar buscar o registro existente
      const { data: existing, error: selectError } = await supabase
        .from('daily_usage')
        .select('messages_sent')
        .eq('tenant_id', tenantId)
        .eq('user_id', userId)
        .eq('usage_date', today)
        .eq('ai_provider', aiProvider)
        .single();

      if (existing) {
        // Se existe, incrementar
        const { error: updateError } = await supabase
          .from('daily_usage')
          .update({ messages_sent: existing.messages_sent + 1 })
          .eq('tenant_id', tenantId)
          .eq('user_id', userId)
          .eq('usage_date', today)
          .eq('ai_provider', aiProvider);

        if (updateError) {
          throw updateError;
        }
      } else {
        // Se não existe, criar novo registro
        const { error: insertError } = await supabase
          .from('daily_usage')
          .insert({
            tenant_id: tenantId,
            user_id: userId,
            usage_date: today,
            ai_provider: aiProvider,
            messages_sent: 1
          });

        if (insertError) {
          throw insertError;
        }
      }

    } catch (error) {
      console.error('❌ Erro ao incrementar uso:', error);
      // Não lançar erro para não bloquear o fluxo
    }
  }

  // =====================================================
  // STRIPE INTEGRATION
  // =====================================================

  /**
   * Criar checkout session do Stripe
   */
  async createStripeCheckout(tenantId: string, planSlug: string, billingCycle: 'monthly' | 'yearly' = 'monthly'): Promise<{ url: string }> {
    try {
      // Buscar plano e seus IDs do Stripe
      const plan = await this.getPlanBySlug(planSlug);
      
      if (!plan) {
        throw new Error(`Plano ${planSlug} não encontrado`);
      }

      // Selecionar o price_id correto baseado no ciclo de faturamento
      const priceId = billingCycle === 'monthly' 
        ? plan.stripePriceIdMonthly 
        : plan.stripePriceIdYearly;

      if (!priceId) {
        throw new Error(`ID de preço do Stripe não configurado para o plano ${planSlug}. Execute o SQL para adicionar os IDs.`);
      }

      console.log('💳 Criando checkout Stripe:', { tenantId, planSlug, billingCycle, priceId });

      // Importar stripe-service dinamicamente
      const { stripeService } = await import('./stripe-service');

      // Criar sessão de checkout
      const baseUrl = window.location.origin;
      
      const session = await stripeService.createCheckoutSession({
        price_id: priceId,
        success_url: `${baseUrl}/?checkout=success`,
        cancel_url: `${baseUrl}/?checkout=cancel`,
        metadata: {
          tenant_id: tenantId,
          plan_slug: planSlug,
          billing_cycle: billingCycle
        }
      });

      console.log('✅ Checkout criado com sucesso:', session.id);

      return {
        url: session.url
      };

    } catch (error) {
      console.error('❌ Erro ao criar checkout Stripe:', error);
      throw error;
    }
  }

  /**
   * Processar webhook do Stripe
   */
  async processStripeWebhook(event: any): Promise<void> {
    try {
      console.log('🔄 Processando webhook Stripe:', event.type);

      switch (event.type) {
        case 'customer.subscription.created':
        case 'customer.subscription.updated':
          await this.handleSubscriptionUpdate(event.data.object);
          break;
        
        case 'customer.subscription.deleted':
          await this.handleSubscriptionCancellation(event.data.object);
          break;
        
        default:
          console.log('ℹ️ Evento Stripe não tratado:', event.type);
      }

    } catch (error) {
      console.error('❌ Erro ao processar webhook Stripe:', error);
    }
  }

  private async handleSubscriptionUpdate(subscription: any): Promise<void> {
    // Implementar lógica de atualização de assinatura
    console.log('🔄 Atualizando assinatura:', subscription.id);
  }

  private async handleSubscriptionCancellation(subscription: any): Promise<void> {
    // Implementar lógica de cancelamento
    console.log('🔄 Cancelando assinatura:', subscription.id);
  }
}

// =====================================================
// FUNÇÕES DE CONVENIÊNCIA
// =====================================================

/**
 * Verificar se usuário pode enviar mensagem (função standalone)
 */
export async function canUserSendMessage(
  tenantId: string, 
  userId: string
): Promise<{ canSend: boolean; reason?: string; usage?: UsageSummary }> {
  try {
    const canSend = await plansService.canSendMessage(tenantId, userId);
    const usage = await plansService.getDailyUsage(tenantId, userId);

    if (!canSend) {
      return {
        canSend: false,
        reason: usage.isUnlimited ? 'Erro interno' : `Limite diário atingido (${usage.limitMessages} mensagens)`,
        usage
      };
    }

    return {
      canSend: true,
      usage
    };
  } catch (error) {
    console.error('❌ Erro ao verificar se pode enviar mensagem:', error);
    return {
      canSend: false,
      reason: 'Erro interno do sistema'
    };
  }
}

/**
 * Processar envio de mensagem (verificar + incrementar)
 */
export async function processMessageSent(
  tenantId: string, 
  userId: string, 
  aiProvider: string = 'gpt'
): Promise<{ success: boolean; reason?: string; usage?: UsageSummary }> {
  try {
    // Verificar se pode enviar
    const checkResult = await canUserSendMessage(tenantId, userId);
    
    if (!checkResult.canSend) {
      return {
        success: false,
        reason: checkResult.reason,
        usage: checkResult.usage
      };
    }

    // Incrementar uso
    await plansService.incrementUsage(tenantId, userId, aiProvider);

    // Buscar uso atualizado
    const updatedUsage = await plansService.getDailyUsage(tenantId, userId);

    return {
      success: true,
      usage: updatedUsage
    };
  } catch (error) {
    console.error('❌ Erro ao processar envio de mensagem:', error);
    return {
      success: false,
      reason: 'Erro interno do sistema'
    };
  }
}

// =====================================================
// INSTÂNCIA SINGLETON
// =====================================================
export const plansService = new SimplePlansService();
